import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  widget,
  WidgetDataType,
  Filter,
  useSearchResultsActions,
  EntityModel,
  SearchResponseFacet,
  useSearchResultsSelectedFilters,
} from '@sitecore-search/react';
import classNames from 'classnames';
import { debounce } from '@sitecore-search/common';
import { Text } from '@sitecore-jss/sitecore-jss-nextjs';
import { AccordionFacets, FacetItem, SearchResultsAccordionFacets } from '@sitecore-search/ui';

import SVG from 'helpers/SVG/SVG';
import ImageWrapper from 'helpers/ImageWrapper/ImageWrapper';
import LinkWrapper from 'helpers/LinkWrapper/LinkWrapper';
import Button from 'helpers/Button/Button';
import { useDictionary } from 'lib/hooks/use-dictionary';
import { useSanitizedSearchResults } from 'lib/utils/token-replacer';

type ExpertsListWidgetProps = {
  initialCount: string;
  verticlePaginationOptions?: {
    loadMoreText: string;
    loadCount: string;
  };
  noResultsText?: string;
  placeholderText?: string;
  filterTypeLabel?: string;
  filters?: Filter;
};

type ExpertProps = {
  name: string;
  image_url: string;
  job_title: string;
  expertise: Array<string>;
  url: string;
};

const ExpertsList = ({
  initialCount,
  verticlePaginationOptions,
  noResultsText,
  filterTypeLabel,
  placeholderText,
}: ExpertsListWidgetProps) => {
  // States
  const [searchBoxValue, setSearchBoxValue] = useState('');
  const [isHidden, setIsHidden] = useState(true);
  const [isAnimating, setIsAnimating] = useState(false);
  const [expertsData, setExpertsData] = useState<{
    expertList: Array<EntityModel>;
    fetchCount: number;
  }>({ expertList: [], fetchCount: 1 });

  const [isFilterPanelVisible, setIsFilterPanelVisible] = useState(false);

  const selectedFacetsCountRef = useRef<number>(0);

  const searchSource = process.env.SEARCH_SOURCE_ID;
  const searchSourceIds = searchSource?.split('|') || [];

  const newCardsRef = useRef<number>(0);

  const {
    sanitizedResults,
    widgetRef,
    query,
    state: { selectedFacets },
    actions: { onKeyphraseChange },
    queryResult: {
      isInitialLoading,
      isFetched,
      data: { total_item = 0, limit = 0, offset = 0, facet: facets = [] } = {},
    },
  } = useSanitizedSearchResults({
    query: (query) =>
      query
        .getRequest()
        .setSearchFacetAll(false)
        .addSearchFacetType({ name: 'expertise' })
        .setSearchSuggestion([{ max: 5, name: 'auto_named_suggester_1', keyphrase_fallback: true }])
        .setSources(searchSourceIds),
    state: {
      itemsPerPage: Number(initialCount),
      page: 1,
    },
  });

  // Handle the scroll enable/disable functionality on filter panel visibility
  useEffect(() => {
    if (typeof window !== 'undefined' && document) {
      if (isFilterPanelVisible && total_item > 0) {
        // Calculate scrollbar width if present to handle the layout shift
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
        document.body.style.paddingRight = `${scrollbarWidth}px`;
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.paddingRight = ''; // Stop scrolling
        document.body.style.overflow = 'auto'; // Allow scrolling
      }
    }
    // Cleanup function
    return () => {
      document.body.style.paddingRight = ''; // Stop scrolling
      document.body.style.overflow = 'auto'; // Ensure overflow is reset
    };
  }, [isFilterPanelVisible]);

  useEffect(() => {
    if (isFilterPanelVisible) {
      setIsHidden(false);
      requestAnimationFrame(() => {
        setIsAnimating(true);
      });
    } else {
      setIsAnimating(false);
    }
  }, [isFilterPanelVisible]);

  useEffect(() => {
    if (!isInitialLoading && sanitizedResults?.content) {
      if (
        offset === 0 ||
        (selectedFacets && selectedFacets.length !== selectedFacetsCountRef.current)
      ) {
        selectedFacetsCountRef.current = selectedFacets?.length || 0;
        setExpertsData({
          fetchCount: 1,
          expertList: sanitizedResults.content,
        });
      } else {
        setExpertsData({
          fetchCount: expertsData.fetchCount + 1,
          expertList: [...expertsData.expertList, ...sanitizedResults.content],
        });

        // Focus on first new card after content updates
        const newCards = document.querySelectorAll('[data-expert-card]');
        const firstNewCard = newCards[newCardsRef.current];
        if (firstNewCard) {
          (firstNewCard as HTMLElement).scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
          });
          (firstNewCard as HTMLElement).setAttribute('tabindex', '0');
          (firstNewCard as HTMLElement).focus();
        }
      }
    }
  }, [offset, sanitizedResults?.content, searchBoxValue]);

  const onKeyphraseChangedDebounced = useMemo(
    () =>
      debounce((e) => {
        query
          .getRequest()
          [e.target.value ? 'setSearchQueryKeyphrase' : 'resetSearchQueryKeyphrase'](
            e.target.value
          );
      }, 200),
    // We don't need to add query as dependency as we need results only if the search input is changed
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onKeyphraseChange]
  );

  const loadMoreResults = () => {
    const _loadCount = Number(verticlePaginationOptions?.loadCount);
    const currentLength = expertsData.expertList.length;
    newCardsRef.current = currentLength;

    if (expertsData.fetchCount >= 1) {
      query.getRequest().setSearchLimit(_loadCount).setSearchOffset(currentLength);
    }
  };

  return (
    <section className="component container spacing-md" ref={widgetRef}>
      {/* Search and Filter Panels */}
      <div className="flex flex-col gap-x-4 gap-y-6 mb-12">
        <div className="flex md:flex-row flex-col gap-4">
          <div className="flex items-start justify-center min-h-[44px] md:min-w-[352px]">
            <div className="relative flex-grow self-stretch ">
              <input
                onChange={(e) => {
                  onKeyphraseChangedDebounced(e);
                  setSearchBoxValue(e.target.value);
                }}
                value={searchBoxValue}
                className={classNames(
                  'border border-light-stroke-primary border-r-0 rounded-l-[28px] pl-4 w-full h-full focus:outline-none focus:border-light-stroke-primary-variant',
                  searchBoxValue ? 'p3-medium' : 'p3'
                )}
                placeholder={placeholderText}
                aria-label="search for expert"
              />
              {/* Clear Button */}
              {searchBoxValue && (
                <button
                  tabIndex={0}
                  className="absolute right-[14px] top-1/2 -translate-y-1/2"
                  onClick={(e) => {
                    setSearchBoxValue('');
                    onKeyphraseChangedDebounced(e);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      setSearchBoxValue('');
                      onKeyphraseChangedDebounced(e);
                    }
                  }}
                  aria-label="clear input"
                >
                  <SVG
                    className="inline-block [&_svg]:w-3 [&_svg]:h-3 text-light-fill-primary-variant"
                    svg="icon-close"
                  />
                </button>
              )}
            </div>
            {/* Search Button */}
            <button
              tabIndex={-1}
              className="p-2 h-11 min-w-[46px] rounded-r-full flex items-center justify-center bg-light-background-gradient hover:bg-light-background-gradient-active focus:bg-light-background-gradient-focus outline-none focus:outline-none"
              aria-label="search expert"
            >
              <SVG
                className="[&_svg]:h-5 [&_svg]:w-5 [&_path]:stroke-light-fill-inverse"
                svg="icon-search2"
              />
            </button>
          </div>
          <div className="flex items-center justify-start flex-grow gap-x-[14px]">
            {/* Filter Toggle */}
            <div
              tabIndex={0}
              className="p-2 flex items-center justify-center gap-2 cursor-pointer rounded-[4px] hover:bg-light-background-neutral-highest focus:outline-none border border-transparent focus:border-light-stroke-primary-variant"
              onClick={() => setIsFilterPanelVisible(!isFilterPanelVisible)}
              onKeyDown={(e) => e.key === 'Enter' && setIsFilterPanelVisible(!isFilterPanelVisible)}
            >
              <SVG
                className="[&_svg]:w-4 [&_svg]:h-4 [&_svg]:md:w-5 [&_svg]:md:h-5 [&_svg]:lg:w-6 [&_svg]:lg:h-6 [&_path]:fill-light-fill-secondary"
                svg="icon-sort-filter"
              />
              <p className="p3-medium text-light-fill-secondary">{filterTypeLabel}</p>
            </div>
            <span className="inline-block flex-grow h-[1px] bg-light-other-grayNeutral10"></span>
          </div>
        </div>
        {selectedFacets && selectedFacets.length > 0 && <FilterChips />}
      </div>

      {total_item === 0 && isFetched && <Text tag="p" field={{ value: noResultsText }} />}

      {isFilterPanelVisible && (
        // Transparent Overlay
        <div
          className="fixed w-screen h-svh top-0 left-0 z-40"
          onClick={() => setIsFilterPanelVisible(false)}
        />
      )}
      {/* Filter Panel */}
      <div
        className={classNames(
          'fixed right-0 top-0 flex ease-in-out duration-100 items-start flex-col gap-2 bg-light-background-neutral z-50 shadow-large',
          isAnimating ? 'translate-x-0' : 'translate-x-full',
          isHidden && !isFilterPanelVisible && 'hidden'
        )}
        onTransitionEnd={() => {
          if (!isFilterPanelVisible) {
            setIsHidden(true);
            setIsAnimating(false);
          }
        }}
      >
        <FilterPanel
          facets={facets}
          filterTypeLabel={filterTypeLabel}
          totalItemsReturned={total_item}
          setIsFilterPanelVisible={setIsFilterPanelVisible}
        />
      </div>

      {total_item > 0 && expertsData.expertList.length > 0 && (
        <ul className="flex flex-wrap flex-col md:flex-row gap-6">
          {expertsData.expertList.map((expertDetails: ExpertProps, i) => (
            <li className="basis-full lg:basis-[calc(100%_/_2_-_24px_/_2_*_(2_-_1))]" key={i}>
              <ExpertCard {...expertDetails} />
            </li>
          ))}
        </ul>
      )}

      {verticlePaginationOptions && (
        <div className="flex items-center justify-center mt-14 [&_button]:!rounded-[4px] [&_button]:!w-auto lg:[&_button]:py-4">
          <Button
            disabled={limit + offset === total_item}
            label={verticlePaginationOptions.loadMoreText}
            type="auxiliary"
            onClick={loadMoreResults}
            onKeyDown={(e) => e.key === 'Enter' && loadMoreResults()}
          />
        </div>
      )}
    </section>
  );
};

//#region Filter Chips
const FilterChips = () => {
  const dictionary = useDictionary();
  const selectedFacetsFromApi = useSearchResultsSelectedFilters();
  const { onRemoveFilter, onClearFilters } = useSearchResultsActions();

  return (
    <ul className="flex flex-wrap gap-x-6 gap-y-4 items-center justify-start">
      {selectedFacetsFromApi?.map((selectedFacet, index) => (
        <li
          tabIndex={0}
          className={classNames(
            'inline-flex items-center justify-center gap-2 p-2 rounded-[4px] cursor-pointer p3-medium',
            'border-2 border-light-stroke-primary-variant hover:border-light-stroke-primary-variant-high hover:underline',
            'focus:outline-none focus:border-light-stroke-primary-variant-highest'
          )}
          key={index}
          onClick={() => onRemoveFilter(selectedFacet)}
          onKeyDown={(e) => e.key === 'Enter' && onRemoveFilter(selectedFacet)}
        >
          {selectedFacet.valueLabel}
          <SVG className="[&_svg]:w-3 [&_svg]:h-3" svg="icon-close" />
        </li>
      ))}
      {selectedFacetsFromApi.length > 0 && (
        <li
          tabIndex={0}
          className="p2 text-light-fill-secondary cursor-pointer "
          onClick={() => onClearFilters()}
          onKeyDown={(e) => e.key === 'Enter' && onClearFilters()}
        >
          {dictionary['Experts.Filter.Clear.All'] || 'Clear all filters'}
        </li>
      )}
    </ul>
  );
};
//#endregion

//#region Filter Panel
const FilterPanel = ({
  facets,
  filterTypeLabel,
  totalItemsReturned,
  setIsFilterPanelVisible,
}: {
  facets: Array<SearchResponseFacet>;
  filterTypeLabel?: string;
  totalItemsReturned?: number;
  setIsFilterPanelVisible: React.Dispatch<boolean>;
}) => {
  const dictionary = useDictionary();
  const { onFacetClick, onClearFilters } = useSearchResultsActions();

  return (
    <SearchResultsAccordionFacets
      defaultFacetTypesExpandedList={['expertise']}
      onFacetValueClick={onFacetClick}
      className="w-full"
    >
      {facets.map((f) => (
        <AccordionFacets.Facet
          facetId={f.name}
          key={f.name}
          className="flex flex-col h-svh max-h-screen"
        >
          <AccordionFacets.Header
            asChild
            className="bg-light-background-neutral p-6 pointer-events-none"
          >
            <AccordionFacets.Trigger className="text-left p1-medium">
              {filterTypeLabel || f.name}
            </AccordionFacets.Trigger>
          </AccordionFacets.Header>
          <AccordionFacets.Content className="flex flex-col flex-1 py-2 px-6 overflow-y-auto bg-background-default-1">
            {
              <AccordionFacets.ValueList className="group list-none pr-4 flex flex-col gap-y-6">
                {f.value
                  .slice()
                  .sort((a, b) => a.text.localeCompare(b.text))
                  .map((v, index: number) => (
                    <FacetItem
                      key={v.id}
                      {...{
                        index,
                        facetValueId: v.id,
                      }}
                      className="p2 flex items-center cursor-pointer gap-2 data-[state=selected]:p2-medium"
                    >
                      <AccordionFacets.ItemCheckbox
                        className={classNames(
                          'flex-none relative rounded-[1px] border-2 border-light-stroke-primary-variant cursor-pointer',
                          'w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6',
                          'aria-checked:bg-light-background-primary aria-checked:hover:border-light-stroke-primary-variant-high aria-checked:hover:bg-light-background-primary-high',
                          'aria-checked:focus:bg-light-background-primary-highest focus:outline-none focus:border-light-stroke-primary-variant-highest'
                        )}
                        aria-label={v.text}
                      >
                        <AccordionFacets.ItemCheckboxIndicator>
                          <SVG
                            className="[&_svg]:p-0.5 [&_svg]:absolute [&_svg]:top-0 [&_path]:fill-light-fill-inverse"
                            svg="icon-thin-check"
                          />
                        </AccordionFacets.ItemCheckboxIndicator>
                      </AccordionFacets.ItemCheckbox>
                      <AccordionFacets.ItemLabel>{v.text}</AccordionFacets.ItemLabel>
                    </FacetItem>
                  ))}
              </AccordionFacets.ValueList>
            }
          </AccordionFacets.Content>
          {/* Button Controls */}
          <div className="w-full p-6 lg:p-8 shadow-small flex flex-col gap-y-4 items-center left-0 bottom-0 bg-light-background-neutral-variant focus:[&_button]:outline-none">
            <div className="w-full text-center p2-medium text-light-fill-secondary [&_button]:border-none">
              <Button
                label={dictionary['Experts.Filter.Panel.Cancel'] || 'Cancel'}
                size="full"
                type="auxiliary"
                onClick={() => {
                  onClearFilters();
                  setIsFilterPanelVisible(false);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    onClearFilters();
                    setIsFilterPanelVisible(false);
                  }
                }}
              />
            </div>
            <div className="w-full focus:[&_button]:border-light-stroke-primary-variant-highest">
              <Button
                label={`${
                  dictionary['Experts.Filter.Panel.ViewResults'] || 'View results'
                } (${totalItemsReturned})`}
                size="full"
                type="auxiliary"
                onClick={() => setIsFilterPanelVisible(false)}
                onKeyDown={(e) => e.key === 'Enter' && setIsFilterPanelVisible(false)}
              />
            </div>
          </div>
        </AccordionFacets.Facet>
      ))}
    </SearchResultsAccordionFacets>
  );
};
//#endregion

//#region Expert Card
const ExpertCard = ({
  image_url = '',
  name = '',
  job_title = '',
  expertise = [],
  url = '',
}: ExpertProps) => {
  return (
    <LinkWrapper
      tabIndex={0}
      data-expert-card
      field={{ href: url }}
      className={classNames(
        'flex items-start gap-4 p-4 bg-light-background-neutral-variant border border-light-stroke-primary rounded no-underline',
        'focus:outline-none focus:no-underline focus:shadow-smallOther',
        'hover:no-underline hover:shadow-largeOther'
      )}
    >
      <div className="w-[107px] h-[198px] sm:w-[264px] sm:h-[336px] lg:w-[186px] lg:h-[251px] self-stretch">
        <ImageWrapper
          field={{ value: { src: image_url } }}
          className="w-full h-full object-cover object-center aspect-[107/198] sm:aspect-[264/336] lg:aspect-[186/251]"
          width="264"
          height="336"
        />
      </div>
      <div className="relative flex flex-[1_0_0] flex-col self-stretch">
        <h4 className="mb-2">{name}</h4>
        <p className="p2 text-light-fill-primary-variant mb-4">{job_title}</p>
        <p className="p3 mb-2">{useDictionary()?.['Experts.Expertise.Label'] || 'Expertise'}</p>

        {expertise.length > 0 &&
          expertise?.map((item, index) => (
            <p key={index} className="p3 mb-2">
              {item}
            </p>
          ))}

        <SVG
          className="[&_svg]:w-[22px] [&_svg]:h-[22px] absolute right-0 bottom-0 text-light-fill-secondary"
          svg="icon-arrow-right"
        />
      </div>
    </LinkWrapper>
  );
};
//#endregion

export const ExpertsListWidget = widget(ExpertsList, WidgetDataType.SEARCH_RESULTS, 'content');
